import { useEffect, useRef, useCallback } from "react";

import { isObservable, isWriteableObservable } from "knockout";

// eslint-disable-next-line local-eslint-rules/no-direct-view-model-hook-access
import { useKoComputed } from "@services/useKoComputed";

const isSetStateAction = <T>(newValue: React.SetStateAction<T>): newValue is (oldValue: T) => T => {
  return typeof newValue === "function" && !isObservable(newValue);
};

/**
 * Given an observable, or undefined, return a useState-like pair of value and setter.
 * @deprecated please migrate away from knockout
 */
export function useKoObservable<T>(observable: KnockoutObservable<T>): [T, React.Dispatch<React.SetStateAction<T>>];

/**
 * @deprecated please migrate away from knockout
 */
export function useKoObservable<T>(
  observable: KnockoutObservable<T> | undefined
): [T | undefined, React.Dispatch<React.SetStateAction<T>>];

/**
 * @deprecated please migrate away from knockout
 */
export function useKoObservable<T>(
  observable: KnockoutObservable<T> | undefined
): [T | undefined, React.Dispatch<React.SetStateAction<T>>] {
  const value = useKoComputed(() => observable && observable(), [observable]);

  const mounted = useRef(true);
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const updateValue: React.Dispatch<React.SetStateAction<T>> = useCallback(
    (newValue) => {
      // we check for whether this is mounted in case this is used in a scenario with asynchrony
      if (!mounted.current) {
        return;
      }
      if (!observable || !isWriteableObservable(observable)) {
        throw new Error("attempt to set a non-observable value");
      }
      if (isSetStateAction(newValue)) {
        observable(newValue(observable.peek()));
      } else {
        observable(newValue);
      }
    },
    [observable]
  );

  return [value, updateValue];
}
